import React, { useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import DatePicker from 'react-datepicker'
import { FiChevronDown, FiCopy, FiTrash2 } from 'react-icons/fi'
import 'react-datepicker/dist/react-datepicker.css'
import { Link } from 'react-router-dom'
import { orderBy } from 'lodash'

import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useListenUserProfileQuery } from 'modules/Users/userApi'
import { useListenCoachOrgSlugQuery } from 'modules/PageBuilder/pageBuilderApi'
import { useSellableProducts } from 'modules/ProductInfo/hooks/useSellableProducts'
import { SELL, STRIPE_SETUP } from 'modules/App/components/Header'
import { Button } from 'common/components/Button/Button'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { DeleteDialogBanner } from 'common/components/DeleteDialogBanner/DeleteDialogBanner'
import { Input, inputBase } from 'common/components/Input/Input'
import { Skeleton } from 'common/components/Skeleton/Skeleton'
import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { Spinner } from 'common/components/Spinner/Spinner'
import { LINK_PLACEMENTS, buildTaggedLink } from 'modules/ProductInfo/utils/shareLinks'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import {
  useCreateTrackedLinkMutation,
  useDeleteTrackedLinkMutation,
  useGetPurchaseReportMutation,
  useListenTrackedLinksQuery,
} from './reportsApi'
import {
  CUSTOM_PRESET_ID,
  LINK_PRESETS,
  TRACKED_LINK_LIMITS,
  buildPurchaseReportCsv,
  getDefaultLinkTags,
  getLinkTags,
  sanitizeUtmValue,
  buildTrackedLinkRecord,
  dateInputsToRange,
  fromDateInputValue,
  getDefaultDateRange,
  getRangeLabel,
  getSourceBreakdown,
  DIRECT_CHANNEL_LABEL,
  OTHER_SOURCE_LABEL,
  REPORT_DATA_START,
  SUBSCRIPTION_PRODUCT_KEY,
  toDateInputValue,
  getReportRows,
  getTrackedLinkChannelLabel,
  getTrackedLinkPageName,
  validateDateInputs,
} from './reportsUtils'

const labelClasses = 'block text-base font-semibold text-tBlack'
const hintClasses = 'text-[13px] text-gray-500'
const messageCardClasses = 'bg-white rounded-2xl px-7 py-5 text-tBlack'
const cardClasses = 'bg-white rounded-2xl overflow-hidden'
const rowBorderClasses = 'border-t border-offWhite'

function Reports() {
  const { userId } = useAuth()
  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''

  const { data: coachOrgSlugData } = useListenCoachOrgSlugQuery({ coachOrgId })
  const coachOrgSlugLoading = coachOrgSlugData === undefined || coachOrgSlugData?.isLoading
  const coachOrgSlug = coachOrgSlugLoading ? '' : coachOrgSlugData || ''

  const { data: trackedLinksData } = useListenTrackedLinksQuery({ coachOrgId }, { skip: !coachOrgId })
  const trackedLinksLoading = trackedLinksData === undefined || trackedLinksData?.isLoading
  const trackedLinks = trackedLinksLoading || !trackedLinksData ? {} : trackedLinksData
  const { products, hasAnyProduct, isLoading: productsLoading } = useSellableProducts({ coachOrgId })

  const [dialogOpen, setDialogOpen] = useState(false)
  const isLoading = coachOrgSlugLoading || trackedLinksLoading || productsLoading
  const canCreateLinks = !isLoading && Boolean(coachOrgSlug) && products.length > 0
  const openDialog = () => setDialogOpen(true)

  return (
    <div className='flex flex-col min-h-screen bg-offWhite px-4 md:px-16'>
      <div className='w-full max-w-[1050px] mx-auto mt-6 md:mt-12 mb-16 flex flex-col gap-10'>
        <h1 className='font-bold text-5xl text-tBlack'>Attribution</h1>
        <DownloadReport coachOrgId={coachOrgId} hasNoProducts={!productsLoading && !hasAnyProduct} />
        <TrackingLinks
          coachOrgId={coachOrgId}
          isLoading={isLoading}
          coachOrgSlug={coachOrgSlug}
          products={products}
          trackedLinks={trackedLinks}
          onNewLink={canCreateLinks ? openDialog : null}
        />
      </div>
      {canCreateLinks && (
        <Dialog open={dialogOpen} setOpen={setDialogOpen}>
          {dialogOpen && (
            <DialogContent
              header='New tracking link'
              disableDefaultContentSizeClasses
              contentClassNames='w-[640px] max-h-[calc(100vh-160px)] max-w-[calc(100vw-16px)] md:max-w-[calc(100vw-48px)]'
            >
              <NewLinkForm
                coachOrgId={coachOrgId}
                coachOrgSlug={coachOrgSlug}
                trackedLinks={trackedLinks}
                products={products}
                onDone={() => setDialogOpen(false)}
              />
            </DialogContent>
          )}
        </Dialog>
      )}
    </div>
  )
}

const SOURCE_COLORS = ['bg-tGreen', 'bg-tBlack', 'bg-tPurple-light']
const sourceColor = (name, index) => {
  if (name === DIRECT_CHANNEL_LABEL) return 'bg-tYellow'
  if (name === OTHER_SOURCE_LABEL) return 'bg-gray-300'
  return SOURCE_COLORS[index - 1] || 'bg-gray-300'
}

const countLabel = (count) => (count === 1 ? '1 new trial or purchase' : `${count} new trials and purchases`)

// A coach who has never made a product has nothing to report yet, so the card only points to
// Products. The server refuses the report with permission-denied for an org without productInfo,
// and an org with productInfo but no products gets no rows.
const REPORT_REFUSED_CODE = 'functions/permission-denied'

function CreateProductMessage() {
  return (
    <p className='text-base text-tBlack'>
      Create your first product to see where your customers come from. Start in{' '}
      <Link to={SELL} className='text-tGreen font-semibold underline'>
        Products
      </Link>
      .
    </p>
  )
}

// rows is undefined while loading, null when the call failed, otherwise the rows. Refetches when
// the picked range changes; an invalid range keeps the last result.
function useSourcesReport(coachOrgId, range) {
  const [getPurchaseReport] = useGetPurchaseReportMutation()
  const [report, setReport] = useState({ rows: undefined, errorCode: null })

  useEffect(() => {
    if (!coachOrgId || validateDateInputs(range.from, range.to)) return undefined
    let ignore = false
    setReport((prev) => ({ ...prev, rows: undefined }))
    const { from, to } = dateInputsToRange(range.from, range.to)
    Promise.resolve(getPurchaseReport({ coachOrgId, from, to })).then((result) => {
      if (ignore) return
      const failed = result?.error || !result
      setReport({ rows: failed ? null : getReportRows(result.data), errorCode: result?.error?.code || null })
    })
    return () => {
      ignore = true
    }
  }, [coachOrgId, getPurchaseReport, range.from, range.to])

  return report
}

function SourcesSummary({ rows: loadedRows, label, invalidMessage }) {
  // A range the server would refuse shows why, never the previous range's numbers
  const rows = invalidMessage ? [] : loadedRows
  const isLoading = rows === undefined
  const breakdown = getSourceBreakdown(rows || [])
  const direct = breakdown.find((slice) => slice.name === DIRECT_CHANNEL_LABEL)

  return (
    <div role='region' aria-label={label} className='flex flex-col gap-4'>
      <div className='flex items-baseline justify-between gap-4'>
        <h3 className='text-lg font-bold text-tBlack'>{label}</h3>
        {rows?.length > 0 && <span className='text-sm text-gray-500'>{countLabel(rows.length)}</span>}
      </div>
      {isLoading && (
        <div data-testid='sources-loading' className='flex flex-col gap-4'>
          <p className='flex items-center gap-2 text-sm text-gray-500'>
            <Spinner className='w-4 h-4 text-gray-200' />
            Loading where your customers came from...
          </p>
          <div className='h-2.5 w-full rounded-full bg-offWhite animate-pulse' />
          <div className='flex gap-6'>
            {[80, 96, 88].map((width) => (
              <div key={width} className='h-4 rounded bg-offWhite animate-pulse' style={{ width }} />
            ))}
          </div>
        </div>
      )}
      {rows === null && (
        <p className='text-sm text-gray-500'>We could not load this date range. Try again in a minute.</p>
      )}
      {invalidMessage && <p className='text-sm text-gray-500'>{invalidMessage}</p>}
      {!invalidMessage && rows?.length === 0 && (
        <p className='text-sm text-gray-500'>No new trials or purchases in this date range.</p>
      )}
      {breakdown.length > 0 && (
        <>
          <div className='flex h-2.5 w-full gap-0.5 rounded-full overflow-hidden bg-offWhite' aria-hidden={true}>
            {breakdown
              .filter((slice) => slice.count > 0)
              .map((slice) => (
                <Tooltip
                  key={slice.name}
                  content={`${slice.name} ${slice.percent}%`}
                  triggerClasses={`block h-full ${sourceColor(slice.name, breakdown.indexOf(slice))}`}
                  triggerStyles={{ width: `${(slice.count / rows.length) * 100}%` }}
                />
              ))}
          </div>
          <ul aria-label='Sources' className='flex flex-wrap gap-x-6 gap-y-2'>
            {breakdown.map((slice, index) => (
              <li key={slice.name} className='flex items-center gap-2 text-sm text-tBlack'>
                <span className={`w-2.5 h-2.5 rounded-sm ${sourceColor(slice.name, index)}`} />
                {slice.name}
                <span className='font-bold'>{slice.percent}%</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {!isLoading && (
        <p className='bg-offWhite rounded-xl px-5 py-4 text-sm leading-relaxed text-gray-500'>
          <strong className='text-tBlack'>{direct ? `${direct.percent}% Direct` : 'Direct'}</strong> means those
          customers used a plain URL, so we don&apos;t know where they came from. Share the tracking links below to
          lower it. It never reaches zero: some people will always type your URL, find you in the App Store, hear about
          you from a friend, or decline cookies before coming back to buy.
        </p>
      )}
    </div>
  )
}

function NewLinkButton({ onClick, children = 'New link', className }) {
  return (
    <Button type='button' size='md' onClick={onClick} className={`gap-2 ${className || ''}`}>
      <span aria-hidden='true' className='text-lg leading-none -mt-px'>
        +
      </span>
      {children}
    </Button>
  )
}

const TRACKING_INFO =
  "Any link to your my.trybe.fit pages with UTM tags is tracked automatically. You don't have to make it here for it to work. This tool is here for convenience and to keep your links organized."

function TrackingLinks({ coachOrgId, isLoading, coachOrgSlug, products, trackedLinks, onNewLink }) {
  const links = orderBy(
    Object.entries(trackedLinks).map(([id, link]) => ({ id, ...link })),
    (link) => link.createdAt ?? 0,
    'desc'
  )

  return (
    <section className='flex flex-col gap-5'>
      <div className='flex flex-wrap items-start justify-between gap-x-6 gap-y-3'>
        <div className='flex flex-col gap-1.5'>
          <div className='flex items-center gap-2.5'>
            <h2 className='font-bold text-3xl text-tBlack'>Tracking links</h2>
            <InfoTooltip label='How tracking works' content={TRACKING_INFO} />
          </div>
          <p className='text-base text-gray-500'>
            Share these instead of plain URLs so each sale is credited to where it came from.
          </p>
        </div>
      </div>
      {isLoading ? (
        <Skeleton className='rounded-2xl min-h-[240px]' />
      ) : !coachOrgSlug ? (
        <p className={messageCardClasses}>
          Your app needs a link handle first. Set it in{' '}
          <Link to={STRIPE_SETUP} className='text-tGreen font-semibold underline'>
            Sell settings
          </Link>
          .
        </p>
      ) : !products.length ? (
        <p className={messageCardClasses}>
          Your tracking links appear here once a product is ready for sale. Set one up in{' '}
          <Link to={SELL} className='text-tGreen font-semibold underline'>
            Products
          </Link>
          .
        </p>
      ) : (
        <>
          <EverydayLinks coachOrgSlug={coachOrgSlug} products={products} />
          <div className={cardClasses}>
            <div className='flex flex-wrap items-start justify-between gap-x-6 gap-y-3 px-7 pt-6 pb-4'>
              <div className='flex flex-col gap-1'>
                <h3 className='text-xl font-bold text-tBlack'>Campaign links</h3>
                <p className='text-sm text-gray-500'>For a launch, promotion, or email campaign you want to measure.</p>
              </div>
              {onNewLink && <NewLinkButton onClick={onNewLink}>New campaign link</NewLinkButton>}
            </div>
            {links.length ? (
              <TrackedLinkList
                coachOrgId={coachOrgId}
                links={links}
                products={products}
              />
            ) : (
              <p className='px-7 py-4 border-t border-offWhite text-sm text-gray-500'>No campaign links yet.</p>
            )}
          </div>
        </>
      )}
    </section>
  )
}

// A native select styled as an input, with its own arrow so it reads as a dropdown and long
// product names never run under the arrow.
function ProductSelect({ products, compact = false, ...props }) {
  return (
    <div className='relative'>
      <select
        {...props}
        css={[
          inputBase,
          tw`py-0 pr-10 appearance-none cursor-pointer truncate`,
          compact && tw`h-10 w-auto min-w-[200px] text-sm font-semibold text-tBlack`,
        ]}
      >
        {products.map((option) => (
          <option key={option.key} value={option.key}>
            {option.name}
          </option>
        ))}
      </select>
      <FiChevronDown
        aria-hidden={true}
        className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tGray-dark'
      />
    </div>
  )
}

// Ready-made links: one row per place, for the product picked in the header. Not saved and no
// campaign name; sales through them show by platform. The Share page offers the same links.
function EverydayLinks({ coachOrgSlug, products }) {
  const [productKey, setProductKey] = useState(products[0]?.key)
  const product = products.find((p) => p.key === productKey) || products[0]
  return (
    <div className={cardClasses}>
      <div className='flex flex-wrap items-start justify-between gap-x-6 gap-y-3 px-7 pt-6 pb-4'>
        <div className='flex flex-col gap-1'>
          <h3 className='text-xl font-bold text-tBlack'>Everyday links</h3>
          <p className='text-sm text-gray-500'>For your bio, channel and page.</p>
        </div>
        {products.length > 1 && (
          <label className='flex items-center gap-2 text-sm font-semibold text-gray-500'>
            Product
            <ProductSelect
              value={product?.key}
              onChange={(e) => setProductKey(e.target.value)}
              products={products}
              compact={true}
            />
          </label>
        )}
      </div>
      <ul aria-label='Everyday links'>
        {LINK_PLACEMENTS.map((place) => (
          <li key={place.id} className={`flex items-center gap-4 px-7 py-3.5 ${rowBorderClasses}`}>
            <div className='flex-1 min-w-0'>
              <p className='text-base font-bold text-tBlack'>{place.label}</p>
              <p className='text-[13px] text-gray-500'>{place.where}</p>
            </div>
            <CopyUrlButton
              url={buildTaggedLink(coachOrgSlug, placementTags(place), product?.path || '')}
              label={`Copy ${place.label} link`}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

const placementTags = ({ utm_source, utm_medium, utm_content }) => ({ utm_source, utm_medium, utm_content })

const emptyForm = {
  presetId: LINK_PRESETS[0].id,
  name: '',
  description: '',
  utmOverrides: {},
}

// Above this many pages, the page choice is a dropdown instead of pills.
const MAX_PAGE_PILLS = 4

function Chip({ selected, children, ...otherProps }) {
  return (
    <button
      type='button'
      className={`px-4 py-2.5 rounded-xl text-sm font-bold bg-white ring-2 ring-inset transition-all ${
        selected ? 'ring-tGreen text-tGreen' : 'ring-tBlack ring-opacity-20 text-tBlack hover:ring-opacity-40'
      }`}
      {...otherProps}
    >
      {children}
    </button>
  )
}

// Resolves false instead of throwing when the browser blocks or lacks the clipboard.
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

// Newest links show first; older ones are one click away, so the report below stays in view
const COLLAPSED_LINK_COUNT = 5

const InputError = tw.p`flex text-xs mt-1 text-tRed`

const DESCRIPTION_PLACEHOLDERS = {
  instagram: 'Bio link for the Black Friday sale',
  youtube: 'Link in my Black Friday video',
  tiktok: 'Bio link for the Black Friday sale',
  facebook: 'Post for the Black Friday sale',
  email: 'Black Friday newsletter',
  custom: 'Podcast ad for the Black Friday sale',
}

const UTM_SUMMARY_PARTS = [
  { key: 'utm_source', label: 'source' },
  { key: 'utm_medium', label: 'medium' },
  { key: 'utm_campaign', label: 'campaign' },
  { key: 'utm_content', label: 'content' },
]

const UTM_PLACEHOLDERS = { utm_campaign: 'black-friday' }

const UTM_FIELDS = [
  { key: 'utm_source' },
  { key: 'utm_medium' },
  { key: 'utm_campaign' },
  { key: 'utm_content', hint: 'Optional' },
]

// The four tags, filled in from the form and editable. A field shows the coach's edit if there is one, otherwise
// the tag the channel or name would give. Values are cleaned up when a field loses focus, and a
// field cleaned to empty or back to its default stops overriding.
function UtmFields({ defaults, overrides, isCustom, onChange, error }) {
  const setOverride = (key, value) => onChange({ ...overrides, [key]: value })
  const onBlur = (key) => {
    const value = sanitizeUtmValue(overrides[key])
    const { [key]: _removed, ...rest } = overrides
    onChange(!value || value === defaults[key] ? rest : { ...rest, [key]: value })
  }
  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
      {UTM_FIELDS.map(({ key, hint }) => (
        <div key={key} className='flex flex-col gap-1.5 min-w-0'>
          <label htmlFor={`reports-${key}`} className='font-mono text-[13px] font-semibold text-tGray-med'>
            {key}
          </label>
          <input
            id={`reports-${key}`}
            value={overrides[key] ?? defaults[key]}
            placeholder={UTM_PLACEHOLDERS[key] || ''}
            onChange={(e) => setOverride(key, e.target.value)}
            onBlur={() => onBlur(key)}
            maxLength={TRACKED_LINK_LIMITS.tag}
            className={`w-full min-w-0 h-11 rounded-[10px] border-0 bg-white ring-1 ring-inset ${
              error?.field === key ? 'ring-red-500' : 'ring-gray-300'
            } focus:ring-tGreen focus:outline-none px-3 font-mono text-sm text-tBlack placeholder:text-tBlack placeholder:text-opacity-40`}
          />
          {error?.field === key ? (
            <InputError role='alert'>{error.message}</InputError>
          ) : (
            hint && <span className='text-xs text-tGray-ml'>{hint}</span>
          )}
        </div>
      ))}
    </div>
  )
}

function NewLinkForm({ coachOrgId, coachOrgSlug, trackedLinks, products, onDone }) {
  const { createAlert } = useAlert()
  const [form, setForm] = useState(() => ({ ...emptyForm, productKey: products[0]?.key || '' }))
  const [error, setError] = useState(null)
  const [createTrackedLink, { isLoading }] = useCreateTrackedLinkMutation()
  const isCustom = form.presetId === CUSTOM_PRESET_ID
  const product = products.find((p) => p.key === form.productKey) || products[0]
  const defaultTags = getDefaultLinkTags(form)
  const linkTags = getLinkTags(form)
  const [utmEditing, setUtmEditing] = useState(false)
  //Custom has no source or medium of its own, so its fields are always open
  const showUtmFields = utmEditing || isCustom

  const setField = (key) => (e) => {
    setError(null)
    setForm((prev) => ({ ...prev, [key]: e.target.value }))
  }

  const onSave = async (e) => {
    e.preventDefault()
    const {
      link,
      error: invalid,
      field: invalidField,
    } = buildTrackedLinkRecord({
      coachSlug: coachOrgSlug,
      trackedLinks,
      ...form,
      product,
    })
    if (invalid) {
      setError({ message: invalid, field: invalidField })
      if (invalidField?.startsWith('utm_')) setUtmEditing(true)
      return
    }
    const result = await createTrackedLink({ coachOrgId, link })
    if (result?.error) {
      setError({ message: 'We could not save the link. Try again in a minute.' })
      return
    }
    const copied = await copyText(link.url)
    createAlert({ text: copied ? 'Link saved and copied' : 'Link saved', type: 'success' })
    onDone()
  }

  return (
    <form
      className='min-h-0 px-6 md:px-8 pt-7 pb-8 flex flex-col gap-6 overflow-y-auto'
      onSubmit={onSave}
      aria-label='New tracking link'
    >
      <div className='flex flex-col gap-1.5'>
        {products.length === 1 && <p className='text-base text-tBlack'>Links go to your {products[0].name} page.</p>}
        {products.length > 1 && (
          <div className='flex flex-col gap-2.5'>
            {products.length > MAX_PAGE_PILLS ? (
              <>
                <label htmlFor='reports-link-page' className={labelClasses}>
                  Which product are you promoting?
                </label>
                <ProductSelect
                  id='reports-link-page'
                  value={product?.key}
                  onChange={setField('productKey')}
                  products={products}
                />
              </>
            ) : (
              <>
                <p className={labelClasses} id='reports-product-label'>
                  Which product are you promoting?
                </p>
                <div className='flex flex-wrap gap-2.5' role='radiogroup' aria-labelledby='reports-product-label'>
                  {products.map((option) => (
                    <Chip
                      key={option.key}
                      role='radio'
                      aria-checked={product?.key === option.key}
                      selected={product?.key === option.key}
                      onClick={() => setField('productKey')({ target: { value: option.key } })}
                    >
                      {option.name}
                    </Chip>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <p className={hintClasses} data-testid='tracking-link-page-url'>
          my.trybe.fit/{coachOrgSlug}
          {product?.path || ''}
        </p>
      </div>
      <div className='flex flex-col gap-1.5'>
        <label htmlFor='reports-link-name' className={labelClasses}>
          Campaign name
        </label>
        <Input
          name='reports-link-name'
          placeholder='Black Friday'
          value={form.name}
          onChange={setField('name')}
          maxLength={TRACKED_LINK_LIMITS.name}
          error={error?.field === 'name'}
          autoFocus
        />
        {error?.field === 'name' ? (
          <InputError role='alert'>{error.message}</InputError>
        ) : (
          <p className={hintClasses}>Shows as the campaign in your report, like black-friday.</p>
        )}
      </div>
      <div className='flex flex-col gap-2.5'>
        <p className={labelClasses} id='reports-preset-label'>
          Where will you share it?
        </p>
        <div className='flex flex-wrap gap-2.5' role='group' aria-labelledby='reports-preset-label'>
          {LINK_PRESETS.map((option) => (
            <Chip
              key={option.id}
              aria-pressed={form.presetId === option.id}
              selected={form.presetId === option.id}
              onClick={() => {
                setField('presetId')({ target: { value: option.id } })
              }}
            >
              {option.label}
            </Chip>
          ))}
        </div>
      </div>
      <div className='flex flex-col gap-1.5'>
        <label htmlFor='reports-link-description' className={labelClasses}>
          Description <span className='font-normal text-gray-500'>(optional)</span>
        </label>
        <Input
          name='reports-link-description'
          placeholder={DESCRIPTION_PLACEHOLDERS[form.presetId] || DESCRIPTION_PLACEHOLDERS.custom}
          value={form.description}
          onChange={setField('description')}
          maxLength={TRACKED_LINK_LIMITS.description}
          error={error?.field === 'description'}
        />
        {error?.field === 'description' && <InputError role='alert'>{error.message}</InputError>}
      </div>
      <div className='flex flex-col gap-2'>
        <div className='flex items-center justify-between gap-4'>
          <p className='text-[13px] font-semibold text-gray-500 uppercase tracking-wide'>UTM tags</p>
          <div className='flex items-center gap-4'>
            {showUtmFields && (
              <button
                type='button'
                onClick={() => setField('utmOverrides')({ target: { value: {} } })}
                className='text-[13px] text-gray-500 hover:text-tBlack'
              >
                Reset to defaults
              </button>
            )}
            {!isCustom && (
              <button
                type='button'
                aria-expanded={showUtmFields}
                onClick={() => setUtmEditing((editing) => !editing)}
                className='text-[13px] font-semibold text-tGreen-dark underline underline-offset-2'
              >
                {showUtmFields ? 'Done' : 'Edit'}
              </button>
            )}
          </div>
        </div>
        {showUtmFields ? (
          <UtmFields
            defaults={defaultTags}
            overrides={form.utmOverrides}
            isCustom={isCustom}
            onChange={(value) => setField('utmOverrides')({ target: { value } })}
            error={error}
          />
        ) : (
          <p data-testid='utm-summary' className='font-mono text-[13px] text-tGray-med break-words'>
            {UTM_SUMMARY_PARTS.filter(({ key }) => key !== 'utm_content' || linkTags.utm_content).map(
              ({ key, label }, index) => (
                <React.Fragment key={key}>
                  {index > 0 && <span className='text-tGray-ml'>{' · '}</span>}
                  {label}{' '}
                  <span className='text-tBlack font-semibold'>
                    {linkTags[key] || (key === 'utm_campaign' ? 'black-friday' : '')}
                  </span>
                </React.Fragment>
              )
            )}
          </p>
        )}
      </div>
      {error && !error.field && (
        <p role='alert' className='text-sm text-tRed -mb-2'>
          {error.message}
        </p>
      )}
      <div className='flex gap-4'>
        <Button type='button' variant='secondary' size='lg' className='flex-1' onClick={onDone}>
          Cancel
        </Button>
        <Button type='submit' size='lg' loading={isLoading} className='flex-1'>
          Save and copy link
        </Button>
      </div>
    </form>
  )
}

function TrackedLinkList({ coachOrgId, links, products }) {
  const { createAlert } = useAlert()
  const [deleteTrackedLink] = useDeleteTrackedLinkMutation()

  const [deletingId, setDeletingId] = useState(null)
  const [showAll, setShowAll] = useState(false)
  const visibleLinks = showAll ? links : links.slice(0, COLLAPSED_LINK_COUNT)
  // A saved link keeps its URL after its product stops being ready for sale, but the storefront
  // page behind it only exists while the product is ready, so the row warns.
  const readyKeys = new Set(products.map((product) => product.key))
  const isLinkReady = (link) => readyKeys.has(link.productKey || SUBSCRIPTION_PRODUCT_KEY)

  const onDelete = async (link) => {
    setDeletingId(link.id)
    const result = await deleteTrackedLink({ coachOrgId, linkId: link.id })
    setDeletingId(null)
    if (result?.error) createAlert({ text: 'We could not delete the link. Try again.', type: 'danger' })
  }

  return (
    <div className='flex flex-col'>
      <ul aria-label='Saved tracking links'>
        {visibleLinks.map((link) => (
          <li key={link.id} className={`flex items-center gap-4 px-7 py-3.5 ${rowBorderClasses}`}>
            <div className='flex-1 min-w-0'>
              <p className='text-base font-bold text-tBlack break-words'>{link.name}</p>
              <p className='text-[13px] text-gray-500 break-words'>
                {getTrackedLinkPageName(link)} · {getTrackedLinkChannelLabel(link)}
                {link.description ? ` · ${link.description}` : ''}
              </p>
              {!isLinkReady(link) && (
                <p className='text-[13px] font-semibold text-orange-500'>
                  Product not ready for sale. This link won&apos;t work until it is.
                </p>
              )}
            </div>
            <CopyUrlButton url={link.url} label={`Copy ${link.name} link`} />
            <Dialog>
              <DialogTrigger
                aria-label={`Delete ${link.name}`}
                className='flex-none p-1.5 rounded-lg text-tGray-ml hover:text-tBlack hover:bg-offWhite transition-colors'
              >
                <FiTrash2 className='w-4 h-4' />
              </DialogTrigger>
              <DialogContent>
                <DeleteDialogBanner
                  text={`This will delete the ${link.name} link`}
                  subtext='People who already have the link can still use it, and its sales stay in your report.'
                  handleDelete={() => onDelete(link)}
                  loading={deletingId === link.id}
                />
              </DialogContent>
            </Dialog>
          </li>
        ))}
      </ul>
      {links.length > COLLAPSED_LINK_COUNT && (
        <button
          type='button'
          onClick={() => setShowAll((all) => !all)}
          className='self-start px-7 py-3.5 text-sm font-semibold text-tGreen-dark underline underline-offset-2'
        >
          {showAll ? 'Show fewer' : `Show all ${links.length} links`}
        </button>
      )}
    </div>
  )
}

function CopyUrlButton({ url, label }) {
  const { createAlert } = useAlert()
  const [copied, setCopied] = useState(false)
  const timer = useRef(null)
  useEffect(() => () => clearTimeout(timer.current), [])

  const onCopy = () =>
    navigator.clipboard.writeText(url).then(() => {
      createAlert({ text: 'Copied to clipboard', type: 'success' })
      setCopied(true)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 1500)
    })

  return (
    <Button
      type='button'
      variant='tertiary'
      size='sm'
      aria-label={label}
      onClick={onCopy}
      className='flex-none w-[118px] gap-2'
    >
      <FiCopy className='w-3.5 h-3.5' />
      {copied ? 'Copied' : 'Copy URL'}
    </Button>
  )
}

const BYTE_ORDER_MARK = String.fromCharCode(0xfeff)

function downloadCsv(csv, filename) {
  // The byte order mark makes Excel read the file as UTF-8.
  const blob = new Blob([`${BYTE_ORDER_MARK}${csv}`], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

const REPORT_INFO =
  "Every new trial and purchase from Aug 15, 2026 onward, with where the buyer came from and whether they kept paying. Purchases that didn't come through a tracking link show as “Direct”. Customers from before Aug 15 aren't included. Looking for revenue? Your Stripe dashboard has revenue, payouts and retention."

function DownloadReport({ coachOrgId, hasNoProducts }) {
  const [range, setRange] = useState(() => getDefaultDateRange())
  const [message, setMessage] = useState(null)
  const [getPurchaseReport, { isLoading }] = useGetPurchaseReportMutation()
  const { rows: summaryRows, errorCode } = useSourcesReport(coachOrgId, range)
  const showCreateProduct = hasNoProducts && (errorCode === REPORT_REFUSED_CODE || summaryRows?.length === 0)

  const onDownload = async () => {
    setMessage(null)
    const invalid = validateDateInputs(range.from, range.to)
    if (invalid) {
      setMessage({ type: 'error', text: invalid })
      return
    }
    const { from, to } = dateInputsToRange(range.from, range.to)
    // The download is the only place that asks for customer emails
    const result = await getPurchaseReport({ coachOrgId, from, to, includeEmails: true })
    if (result?.error || !result) {
      setMessage({
        type: 'error',
        text: 'We could not load your report. Try again in a minute, or email support@trybe.do if it keeps happening.',
      })
      return
    }
    const rows = getReportRows(result.data)
    if (!rows.length) {
      setMessage({ type: 'info', text: 'No new customers in this date range.' })
      return
    }
    downloadCsv(buildPurchaseReportCsv(rows), `customer-sources-${range.from}-to-${range.to}.csv`)
  }

  return (
    <section className='flex flex-col gap-5' aria-labelledby='reports-attribution-heading'>
      <div className='flex items-center gap-2.5'>
        <h2 id='reports-attribution-heading' className='font-bold text-3xl text-tBlack'>
          Customer sources
        </h2>
        <InfoTooltip label='What the report covers' content={REPORT_INFO} />
      </div>
      <p className='-mt-3 text-base text-gray-500'>Where your new customers came from, free trials included.</p>
      {showCreateProduct ? (
        <div className={messageCardClasses}>
          <CreateProductMessage />
        </div>
      ) : (
        <div className='bg-white rounded-2xl p-6 md:p-8'>
          <SourcesSummary
            rows={summaryRows}
            label={getRangeLabel(range)}
            invalidMessage={validateDateInputs(range.from, range.to)}
          />
          <div className='flex flex-wrap items-end gap-6 mt-6 pt-6 border-t border-offWhite'>
            <div className='flex flex-col gap-1.5 w-[200px]'>
              <label htmlFor='reports-from' className={labelClasses}>
                From
              </label>
              <div>
                <DatePicker
                  id='reports-from'
                  name='reports-from'
                  selected={fromDateInputValue(range.from)}
                  onChange={(date) => date && setRange((prev) => ({ ...prev, from: toDateInputValue(date) }))}
                  minDate={REPORT_DATA_START}
                  maxDate={fromDateInputValue(range.to) || new Date()}
                  placeholderText='mm/dd/yyyy'
                  wrapperClassName='w-full'
                  css={[inputBase]}
                  popperProps={{ placement: 'bottom-start', strategy: 'fixed' }}
                />
              </div>
            </div>
            <div className='flex flex-col gap-1.5 w-[200px]'>
              <label htmlFor='reports-to' className={labelClasses}>
                To
              </label>
              <div>
                <DatePicker
                  id='reports-to'
                  name='reports-to'
                  selected={fromDateInputValue(range.to)}
                  onChange={(date) => date && setRange((prev) => ({ ...prev, to: toDateInputValue(date) }))}
                  minDate={fromDateInputValue(range.from) || REPORT_DATA_START}
                  maxDate={new Date()}
                  placeholderText='mm/dd/yyyy'
                  wrapperClassName='w-full'
                  css={[inputBase]}
                  popperProps={{ placement: 'bottom-start', strategy: 'fixed' }}
                />
              </div>
            </div>
            <Button type='button' size='lg' loading={isLoading} onClick={onDownload}>
              Download CSV
            </Button>
          </div>
          {message && (
            <p
              role={message.type === 'error' ? 'alert' : 'status'}
              className={`text-sm mt-3 ${message.type === 'error' ? 'text-tRed' : 'text-tBlack'}`}
            >
              {message.text}
            </p>
          )}
        </div>
      )}
    </section>
  )
}

// Opens on hover like other dashboard tooltips, and on click so it also works on touch screens.
// A tap outside closes it.
function InfoTooltip({ label, content, align = 'start' }) {
  const [open, setOpen] = useState(false)
  return (
    <Tooltip
      content={content}
      open={open}
      onOpenChange={setOpen}
      side='bottom'
      align={align}
      className='bg-tBlack text-white text-[13px] text-left leading-snug font-normal px-3 py-2.5 rounded-md z-[999] w-[360px] max-w-[calc(100vw-32px)]'
    >
      <button
        type='button'
        aria-label={label}
        onClick={(e) => {
          // The tooltip trigger closes on click, so the click must not reach it.
          e.stopPropagation()
          setOpen(true)
        }}
        className='flex-none inline-flex items-center justify-center w-5 h-5 rounded-full border-[1.5px] border-tGray-ml font-serif font-bold text-xs leading-none text-tGray-med cursor-help'
      >
        i
      </button>
    </Tooltip>
  )
}

export default Reports
